import React, { useEffect, useState } from 'react'
import { ReactComponent as StarsBackgroundSvg } from '../../../assets/img/metaberry_ui/daily_goal_bg.svg'
import { ReactComponent as WhiteStarsBackgroundSvg } from '../../../assets/img/metaberry_ui/white-stars.svg'
import { ReactComponent as ShieldSvg } from '../../../assets/img/metaberry_ui/level_shield.svg'
import { ReactComponent as ShadowSvg } from '../../../assets/img/metaberry_ui/shadow.svg'
import { ReactComponent as CoinsSvg } from '../../../assets/img/metaberry_ui/coins.svg'
import { ReactComponent as RibbonSvg } from '../../../assets/img/metaberry_ui/ribbon_blue.svg'

import useSound from '../../views/MetaberryView/hooks/useSound'
import './informationPopup.scss'
import PracticeReward from '../../../pages/PracticeReward/PracticeReward'
import { getSoundAssistance } from '../../../services/settingsService'
import DynamicTextContainer from '../../atoms/DynamicTextContainer/DynamicTextContainer'
import PropTypes from 'prop-types'
import { ReactComponent as CloseIcon } from '../../components/PopupManager/assets/close_icon.svg'

const CoinsGroup = ({ coins }) => {
  return (
    <div className="information-coins-group">
      <StarsBackgroundSvg className="information-coins-group__background" />
      <CoinsSvg className="information-coins-group__coins-image" />
      <RibbonSvg className="information-coins-group__ribbon-image" />
      <div className="information-coins-group__coins-number">{'+' + coins}</div>
    </div>
  )
}

CoinsGroup.propTypes = {
  coins: PropTypes.number.isRequired
}

const StarsBackgroundComponents = {
  levelUp: '',
  generalPopup: StarsBackgroundSvg,
  endOfChallenge: WhiteStarsBackgroundSvg
}

const LevelGroup = ({ children, context }) => {
  const StarsBackgroundSvg = StarsBackgroundComponents[context]
  return (
    <div className="information-level-group">
      <StarsBackgroundSvg className="information-level-group__background" />
      {children}
      <ShadowSvg
        preserveAspectRatio="none"
        className="information-level-group__shadow"
      />
    </div>
  )
}

const BatteryGroup = ({ children, context }) => {
  const StarsBackgroundSvg = StarsBackgroundComponents[context]
  return (
    <div className="information-battery-group">
      <StarsBackgroundSvg className="information-battery-group__background" />
      {children}
      <ShadowSvg
        preserveAspectRatio="none"
        className="information-battery-group__shadow"
      />
    </div>
  )
}

LevelGroup.propTypes = {
  children: PropTypes.node.isRequired,
  context: PropTypes.oneOf(['levelUp', 'generalPopup', 'endOfChallenge'])
    .isRequired
}

const InformationPopup = ({
  message,
  level,
  coins,
  closeAction,
  buttonText,
  buttonAction,
  showCloseIcon,
  practiceProgressData,
  setPracticeBaseData,
  earnedPiecesAux,
  earnedCoinsAux,
  earnedExperienceAux,
  isChallengeEnd,
  batteryIcon,
  completeTitle,
  className,
  audioFile
}) => {
  const { renderSoundFXController, playSoundEffect } = useSound()

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [levelFontSize, setLevelFontSize] = useState(0)
  const [levelTop, setLevelTop] = useState(0)

  useEffect(() => {
    if (level) {
      playSoundEffect('levelUp')
    }

    if (audioFile) {
      if (getSoundAssistance()) {
        setTimeout(() => playSoundEffect(audioFile), level ? 2100 : 0) // Para dar tiempo al sonido anterior (levelUp)
      }
    }

    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (level) {
      let newFontSize = Math.max(-13 * level.toString().length + 85, 24) // Min font size 24px
      let newTop = 8 * level.toString().length - 2

      if (windowWidth < 300) {
        newFontSize *= 0.8
        newTop *= 0.8
      }

      setLevelFontSize(newFontSize)
      setLevelTop(newTop)
    }
  }, [windowWidth, level?.length])

  const handleClickExterior = (event) => {
    if (closeAction && event.target === event.currentTarget) {
      closeAction && closeAction()
    }
  }

  return (
    <>
      {renderSoundFXController()}
      {!isChallengeEnd && (
        <div className="information-popup__backdrop" onClick={closeAction} />
      )}

      <div
        className={`information-popup ${className}`}
        onClick={handleClickExterior}
        role="dialog"
      >
        {showCloseIcon && (
          <div
            className="onboarding-popup__close-icon-new"
            onClick={closeAction}
          >
            <CloseIcon />
          </div>
        )}

        {level && (
          <LevelGroup context="generalPopup">
            <>
              <ShieldSvg className="information-level-group__shield" />
              <div className="information-level-group__level">
                <div
                  style={{
                    fontSize: levelFontSize + 'px',
                    top: levelTop - 2
                  }}
                >
                  {level}
                </div>
              </div>
            </>
          </LevelGroup>
        )}

        {batteryIcon && (
          <BatteryGroup context="endOfChallenge">
            <div className="practice-complete-view__battery-icon">
              {batteryIcon}
            </div>
          </BatteryGroup>
        )}

        {coins && <CoinsGroup coins={coins} />}

        <DynamicTextContainer
          level={level}
          message={message}
          batteryIcon={batteryIcon}
          completeTitle={completeTitle}
          coins={coins}
          audioFile={audioFile}
          playSoundEffect={playSoundEffect}
        />

        {practiceProgressData && (
          <PracticeReward
            practiceProgressData={practiceProgressData}
            setPracticeBaseData={setPracticeBaseData}
            earnedPiecesAux={earnedPiecesAux}
            earnedCoinsAux={earnedCoinsAux}
            earnedExperienceAux={earnedExperienceAux}
          />
        )}

        {(closeAction || buttonAction) && !level && (
          <div className="information-popup__buttons">
            {buttonAction && (
              <button
                className="information-popup_button"
                onClick={buttonAction}
              >
                {buttonText}
              </button>
            )}
            {(closeAction || buttonAction) && !level && !coins && (
              <button
                className="information-popup_button"
                onClick={closeAction}
              >
                Close
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

InformationPopup.propTypes = {
  message: PropTypes.string,
  level: PropTypes.number,
  coins: PropTypes.number,
  closeAction: PropTypes.func,
  buttonText: PropTypes.string,
  buttonAction: PropTypes.func,
  showCloseIcon: PropTypes.bool,
  practiceProgressData: PropTypes.object,
  setPracticeBaseData: PropTypes.func,
  earnedPiecesAux: PropTypes.number,
  earnedCoinsAux: PropTypes.number,
  earnedExperienceAux: PropTypes.number,
  isChallengeEnd: PropTypes.bool,
  batteryIcon: PropTypes.node,
  completeTitle: PropTypes.string,
  className: PropTypes.string,
  audioFile: PropTypes.string
}

export default InformationPopup
